import { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { DateRange } from '@robocorp/components';
import { TimeRangeValues } from '~/constants/timeRanges';
import { useCurrentOrganization } from '~/queries/organization';
import { useUsageQuery } from '~/queries/billing';
import { getTimezoneValue } from '~/utils/timezoneHelpers';
import { useLocalStorage } from '~/hooks/useStorage';
import { getPresetTimeRanges } from '~/utils/timeRangeHelpers';

const persistedPresetSchema = z
  .object({
    preset: z.nativeEnum(TimeRangeValues),
    range: z
      .object({
        from: z.preprocess((value) => (value ? new Date(String(value)) : undefined), z.date()),
        to: z
          .preprocess((value) => (value ? new Date(String(value)) : undefined), z.date())
          .optional(),
      })
      .optional(),
  })
  .optional();

const parseBillingDate = (maybeDate?: string): Date | undefined => {
  if (maybeDate === undefined) return undefined;
  const date = new Date(maybeDate);
  return date.toString() !== 'Invalid Date' ? date : undefined;
};

const getDefaultPreset = (hasBillingPeriod: boolean) =>
  hasBillingPeriod ? TimeRangeValues.CURRENT_BILLING_PERIOD : TimeRangeValues.THIS_MONTH;

const isPresetOutOfSync = (preset: TimeRangeValues, withBillingPreset: boolean) =>
  preset === TimeRangeValues.THIS_MONTH || preset === TimeRangeValues.CURRENT_BILLING_PERIOD
    ? preset !== getDefaultPreset(withBillingPreset)
    : false;

/**
 * Preset validation logic:
 * - must have valid "customRange" when using together with "CUSTOM" preset - otherwise will reset to default value
 * - if billing information is present use that instead of current mont preset
 */
const getNextPreset = (
  preset: TimeRangeValues,
  withBillingPreset: boolean,
  customRange?: DateRange,
) =>
  preset === TimeRangeValues.THIS_MONTH ||
  preset === TimeRangeValues.CURRENT_BILLING_PERIOD ||
  (preset === TimeRangeValues.CUSTOM && !customRange)
    ? getDefaultPreset(withBillingPreset)
    : preset;

interface ConfigurationSettings {
  maxFrom?: Date | string;
}

/**
 * Returns base configuration for time range hooks
 */
const useTimeRangeConfiguration = ({ maxFrom }: ConfigurationSettings) => {
  const { data: currentOrganization } = useCurrentOrganization();
  const organizationId = currentOrganization?.id;

  const { timezone: organizationDetailsTimezone } = currentOrganization?.details ?? {};
  const timezone = useMemo(
    () =>
      organizationDetailsTimezone
        ? (getTimezoneValue(organizationDetailsTimezone)?.value ?? 'local')
        : 'local',
    [organizationDetailsTimezone],
  );

  const {
    data: billingUsage,
    refetch: fetchBillingUsage,
    isFetched: usageIsFetched,
    isInitialLoading: usageIsLoading,
  } = useUsageQuery(organizationId as string, {
    enabled: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const billingStart = useMemo(
    () => parseBillingDate(billingUsage?.billingPeriodStartTs),
    [billingUsage?.billingPeriodStartTs],
  );
  const billingEnd = useMemo(
    () => parseBillingDate(billingUsage?.billingPeriodEndTs),
    [billingUsage?.billingPeriodEndTs],
  );

  const withBillingPreset = useMemo(
    () => billingStart !== undefined && billingEnd !== undefined,
    [billingStart, billingEnd],
  );

  const shouldFetchUsage = useMemo(() => {
    const canManageBilling = currentOrganization?.permissions?.includes('manage_billing') ?? false;
    return canManageBilling && !(usageIsLoading || usageIsFetched);
  }, [currentOrganization?.permissions, usageIsFetched, usageIsLoading]);

  useEffect(() => {
    if (shouldFetchUsage) fetchBillingUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetchUsage]);

  const getPresetConfig = useCallback(
    (value: TimeRangeValues, customRange?: DateRange) => {
      const newPreset = getNextPreset(value, withBillingPreset, customRange);
      return {
        ...getPresetTimeRanges({
          preset: newPreset,
          customRange,
          billingStart,
          billingEnd,
          timezone,
          maxFrom,
        }),
        preset: newPreset,
      };
    },
    [withBillingPreset, billingStart, billingEnd, timezone, maxFrom],
  );

  return {
    withBillingPreset,
    timezone,
    getPresetConfig,
    isLoading: shouldFetchUsage || usageIsLoading,
  };
};

export type KnownTimeRangeValues = Exclude<TimeRangeValues, TimeRangeValues.CUSTOM>;

interface KnownTimeRangeSettings {
  preset?: KnownTimeRangeValues;
  range?: undefined;
}

interface CustomTimeRangeSettings {
  preset: TimeRangeValues.CUSTOM;
  range: Exclude<DateRange, undefined>;
}

export type TimeRangeSettings = ConfigurationSettings &
  (KnownTimeRangeSettings | CustomTimeRangeSettings);

export type PersistedTimeRangeSettings = {
  storageKey: string;
  sync?: boolean;
} & ConfigurationSettings &
  KnownTimeRangeSettings;

interface TimeRangeState {
  preset: TimeRangeValues;
  range: DateRange;
  timestamp: Date;
}

/**
 * Use extended hook under `import { useTimeRange } from '~/hooks';` in components.
 * Root version of time range provider.
 */
export const useTimeRangeProvider = ({
  preset: initialValue = TimeRangeValues.THIS_MONTH,
  range: initialRange,
  ...configurationSettings
}: TimeRangeSettings) => {
  const { withBillingPreset, getPresetConfig, ...configurationArgs } =
    useTimeRangeConfiguration(configurationSettings);
  const [config, setConfig] = useState<TimeRangeState>(getPresetConfig(initialValue, initialRange));

  const { preset, range, timestamp } = useMemo(
    () =>
      isPresetOutOfSync(config.preset, withBillingPreset)
        ? getPresetConfig(getDefaultPreset(withBillingPreset))
        : config,
    [config, getPresetConfig, withBillingPreset],
  );

  const onPresetChange = useCallback(
    (value: TimeRangeValues, dateRange?: DateRange) => setConfig(getPresetConfig(value, dateRange)),
    [getPresetConfig],
  );

  return { preset, range, timestamp, onPresetChange, withBillingPreset, ...configurationArgs };
};

const serializeTimeRange = ({ preset, range }: TimeRangeState) => {
  try {
    if (preset === TimeRangeValues.CUSTOM) {
      return JSON.stringify({ preset, range });
    }
    return JSON.stringify({ preset });
  } catch (_error) {
    throw new Error('failed to serialize time range preset');
  }
};

const deserializeTimeRange = (value: string | undefined) => {
  if (!value) return undefined;
  try {
    const data = JSON.parse(value);
    const parsed = persistedPresetSchema.safeParse(data);
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Persisted version of time range provider.
 * Used together with TimeRangeProvider.Persisted and `import { useContextTimeRange } from '~/hooks';`
 */
export const usePersistedTimeRangeProvider = ({
  preset: initialValue = TimeRangeValues.THIS_MONTH,
  storageKey,
  sync,
  ...configurationSettings
}: PersistedTimeRangeSettings) => {
  const { withBillingPreset, getPresetConfig, ...configurationArgs } =
    useTimeRangeConfiguration(configurationSettings);

  const { storageValue: config, setStorageValue: setConfig } = useLocalStorage<TimeRangeState>({
    initialValue: getPresetConfig(initialValue),
    key: storageKey,
    serializer: serializeTimeRange,
    deserializer: deserializeTimeRange,
    sync,
  });

  const { preset, range, timestamp } = useMemo(() => {
    if (isPresetOutOfSync(config.preset, withBillingPreset))
      return getPresetConfig(getDefaultPreset(withBillingPreset));
    return !config.timestamp
      ? getPresetConfig(config.preset || initialValue, config.range)
      : config;
  }, [config, getPresetConfig, initialValue, withBillingPreset]);

  const onPresetChange = useCallback(
    (value: TimeRangeValues, dateRange?: DateRange) => setConfig(getPresetConfig(value, dateRange)),
    [getPresetConfig, setConfig],
  );

  return { preset, range, timestamp, onPresetChange, withBillingPreset, ...configurationArgs };
};
